import { useReactiveVar } from '@apollo/client'
import { makeVar } from '@apollo/client/cache'
import { IGetLatestDealVersionResponse } from 'queries/getLatestDealVersion'
import { IGetLatestSubmitResponse } from 'queries/getLatestSubmit'
import { IDeal } from 'queries/searchDeal'
import { IDealInfoAPIResponse } from 'ts/interfaces/deal/deal.interfaces'

export type latestDealVersionType = {
  dealId?: number
  dealVersionNumber?: number
  dealNumber?: number
  currentVersionFlag?: string
  hierarchyCode?: string
  businessModelCode?: string
  controlCountryCode?: string
}

export enum DEAL_STATUS {
  'OPEN' = 'UNN',
  'AUTHORIZED' = 'FNN',
  'APPROVED' = 'FSN',
  'PENDING_VALIDATION' = 'FSP',
  'INVALID' = 'FSR',
  'PARTIALLY_QUOTED' = 'FSQ',
  'QUOTED' = 'FSA',
  'DENIED' = 'UDN',
  'CANCELLED' = 'CANCELLED',
}

export enum BDME_CODE {
  'NOT_APPROVED' = 'N',
  'APPROVED' = 'A',
  'APPROVED_DISTRIBUTION' = 'S',
  'PRICING_DENIED' = 'D',
}

export type latestDealType = {
  dealId?: number
  dealNumber?: number
  bdmeCode?: BDME_CODE
  dealVersionNumber?: number
  versionStatus?: DEAL_STATUS
  dealLockFlag?: string
  dealLockUser?: string
  currentVersionFlag?: string
  isDealExpired?: boolean
  dealType?: string
  freezeFlag?: string
  businessModelCode?: string
  controlCountryCode?: string
}

export type latestDealSubmitType = {
  id?: number
  versionNumber?: number
  status?: string
  updatedBy?: string | null
  createdBy?: string
  createdDate?: string
  updatedDate?: string | null
}

export const latestDealVar = makeVar<latestDealType>({})

export const getEmailContentVersionVar = makeVar<number | null>(null)

export const setLatestDeal = (latestDealInfo: IDeal) => {
  const {
    bdId,
    dealNumber,
    dealVersionNumber,
    versionStatus,
    dealLockFlag,
    dealLockUser,
    currentVersionFlag,
    isDealExpired,
    dealType,
    freezeFlag,
    bdmeCode,
    businessModelCode,
    controlCountryCode,
  } = latestDealInfo
  return latestDealVar({
    dealId: bdId,
    dealNumber,
    dealVersionNumber,
    versionStatus,
    bdmeCode,
    dealLockFlag,
    dealLockUser,
    currentVersionFlag,
    isDealExpired,
    dealType,
    freezeFlag,
    businessModelCode,
    controlCountryCode,
  })
}
export const getLatestDeal = () => {
  return latestDealVar()
}

export const setEmailContentVersion = (version: number | null) => {
  return getEmailContentVersionVar(version)
}

export const getEmailContentVersion = () => getEmailContentVersionVar()

export const resetLatestDealVar = () => {
  latestDealVar({})
}

export const latestDealVersionVar = makeVar<latestDealVersionType>({})

const isLatestDealVersionChanged = (latestDealVersion: IGetLatestDealVersionResponse) => {
  const {
    dealId,
    dealVersionNumber,
    dealNumber,
    currentVersionFlag,
    hierarchyCode,
    businessModelCode,
    controlCountryCode,
  } = latestDealVersion.latestDeal
  const latestDeal = latestDealVersionVar()
  return (
    latestDeal?.dealId !== dealId ||
    latestDeal?.dealVersionNumber !== dealVersionNumber ||
    latestDeal?.dealNumber !== dealNumber ||
    latestDeal?.currentVersionFlag !== currentVersionFlag ||
    latestDeal?.hierarchyCode !== hierarchyCode ||
    latestDeal?.businessModelCode !== businessModelCode ||
    latestDeal?.controlCountryCode !== controlCountryCode
  )
}

export const setlatestDealVersion = (latestDealVersion: IGetLatestDealVersionResponse) => {
  if (isLatestDealVersionChanged(latestDealVersion)) {
    const {
      dealId,
      dealVersionNumber,
      dealNumber,
      currentVersionFlag,
      hierarchyCode,
      businessModelCode,
      controlCountryCode,
    } = latestDealVersion.latestDeal
    return latestDealVersionVar({
      dealId,
      dealNumber,
      dealVersionNumber,
      currentVersionFlag,
      hierarchyCode,
      businessModelCode,
      controlCountryCode,
    })
  }
}

export const latestDealSubmitVar = makeVar<latestDealSubmitType | null>(null)

export const setlatestDealSubmit = (latestDealSubmit: IGetLatestSubmitResponse | null) => {
  if (latestDealSubmit?.latestSubmitByDeal) {
    const { id, versionNumber, status, updatedBy, createdBy, createdDate, updatedDate } =
      latestDealSubmit?.latestSubmitByDeal

    return latestDealSubmitVar({
      id,
      versionNumber,
      status,
      updatedBy,
      createdBy,
      createdDate,
      updatedDate,
    })
  }
  return latestDealSubmitVar(null)
}

export const dealInfoVar = makeVar<IDealInfoAPIResponse>({} as IDealInfoAPIResponse)

export const setDealInfo = (dealInfo: IDealInfoAPIResponse) => {
  const { regionalSettings, dealStatus, dealSummary, dealDates } = dealInfo
  return dealInfoVar({
    regionalSettings,
    dealStatus,
    dealSummary,
    dealDates,
  })
}
export const useDealInfo = (): IDealInfoAPIResponse => {
  return useReactiveVar(dealInfoVar)
}
